import React, { useContext } from 'react';
import {
  Badge,
  Box,
  Container,
  Stack,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  ChecklistRtl as ChecklistRtlIcon,
  FactCheck as QaListIcon,
  StickyNote2Outlined as MemoIcon,
  Inventory2Outlined as ConsumableIcon,
  FolderSharedOutlined as CommonFilesIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { ThemeContext } from '../../App';
import UserProfileMenu from '../../components/UserProfileMenu';
import CurrentSiteChip from './CurrentSiteChip';
import SearchBar from './SearchBar';
import { useDashboardTokens, dashboardTokens } from './tokens';
import usePermissions from '../../hooks/usePermissions';
import useMemoSummary, { RECENT_DAYS } from './hooks/useMemoSummary';

interface DashboardHeaderProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onCreateClick: () => void;
}

/**
 * Swiss/Editorial 대시보드 헤더.
 *
 * 구성:
 *  - overline "SITE OPERATIONS" (uppercase, letter-spacing)
 *  - display title "현장·배포 통합 관제"
 *  - 검색 + "신규 현장" + 테마 토글 + 프로필 메뉴 (우측 정렬)
 *
 * 기존 `ThemeContext`, `UserProfileMenu`, `SearchBar` 를 그대로 재사용.
 * 토큰은 로컬 `dashboardTokens` 만 참조, 프로젝트 전역 테마는 건드리지 않음.
 *
 * site-deploy-tab-removal: 기존 [현장관리][배포] 탭은 제거됨. 배포는 현장
 * 진입 후 사이드바의 '배포' 섹션에서 수행 (project-scope deploy).
 */
const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  searchValue,
  onSearchChange,
  onCreateClick,
}) => {
  const tokens = useDashboardTokens();
  const { mode, toggleTheme } = useContext(ThemeContext);
  const { canAdmin } = usePermissions();
  const navigate = useNavigate();
  const memos = useMemoSummary();

  return (
    <Box
      component="header"
      sx={{
        bgcolor: tokens.colors.surface,
        borderBottom: `1px solid ${tokens.colors.border}`,
        pt: dashboardTokens.space.md / 8,
        pb: dashboardTokens.space.md / 8,
      }}
    >
      <Container maxWidth={false}>
        {/* 모바일(md 미만)에서는 우측 영역(검색+버튼)이 타이틀 아래로 내려가고,
            검색창은 전체 폭을 차지한다. flexWrap + rowGap 으로 자연 줄바꿈. */}
        <Stack
          direction="row"
          alignItems="flex-end"
          justifyContent="space-between"
          spacing={dashboardTokens.space.md / 8}
          sx={{ flexWrap: 'wrap', rowGap: 1.5 }}
        >
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 0.5 }}
            >
              <Typography
                component="p"
                sx={{
                  fontSize: tokens.type.caption.size,
                  fontWeight: tokens.type.caption.weight,
                  letterSpacing: tokens.type.caption.spacing,
                  textTransform: 'uppercase',
                  color: tokens.colors.muted,
                }}
              >
                SITE OPERATIONS
              </Typography>
              {/* current-site-chip: company 호스트에서 현재 구동 중인 사이트(banpo/central/osong).
                  30초 폴링 + visibility change 시 즉시 재조회. */}
              <CurrentSiteChip />
            </Stack>
            <Typography
              component="h1"
              onClick={() => navigate('/projects')}
              role="link"
              tabIndex={0}
              sx={{
                fontSize: tokens.type.h1.size,
                fontWeight: tokens.type.h1.weight,
                letterSpacing: tokens.type.h1.spacing,
                color: tokens.colors.text,
                m: 0,
                cursor: 'pointer',
                width: 'fit-content',
                '&:hover': { color: tokens.colors.accent },
              }}
            >
              현장 관리
            </Typography>
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ flexWrap: 'wrap', rowGap: 1, minWidth: 0, maxWidth: '100%' }}
            useFlexGap
          >
            <Box sx={{ width: { xs: '100%', sm: 280 } }}>
              <SearchBar
                value={searchValue}
                onChange={onSearchChange}
                placeholder="현장 이름 또는 위치 검색"
              />
            </Box>
            {/* 전체 공지사항 — 날짜 없는 공지는 캘린더에 뜨지 않으므로, 어느 현장에
                무엇을 적어뒀는지 찾는 경로가 하나는 있어야 한다. 조회는 권한과
                무관하다.

                건수는 적지 않는다. 대신 최근 1주 안에 올라온 공지가 있으면 느낌표
                배지를 띄운다 — 눌러 보지 않으면 새 공지가 생긴 줄도 모르기 때문이다. */}
            <Badge
              color="error"
              badgeContent="!"
              invisible={!memos.hasRecent}
              data-testid="memos-recent-badge"
              sx={{ '& .MuiBadge-badge': { top: 6, right: 4, fontWeight: 700 } }}
            >
              <Button
                variant="text"
                startIcon={<MemoIcon />}
                onClick={() => navigate('/memos')}
                data-testid="all-memos-btn"
                aria-label={
                  memos.hasRecent
                    ? `전체 공지사항 — 최근 ${RECENT_DAYS}일 내 새 공지 있음`
                    : undefined
                }
                sx={{
                  fontWeight: 600,
                  textTransform: 'none',
                  color: tokens.colors.text,
                  '&:hover': { color: tokens.colors.accent },
                }}
              >
                전체 공지사항
              </Button>
            </Badge>
            {/* 전체 소모품 — 회사 전체 소모품과 현장별 소모품 메모. 조회는 누구나. */}
            <Button
              variant="text"
              startIcon={<ConsumableIcon />}
              onClick={() => navigate('/consumables')}
              data-testid="all-consumables-btn"
              sx={{
                fontWeight: 600,
                textTransform: 'none',
                color: tokens.colors.text,
                '&:hover': { color: tokens.colors.accent },
              }}
            >
              전체 소모품
            </Button>
            {/* 공통 파일 보관함 — 현장마다가 아니라 모든 현장이 함께 쓰는 파일. */}
            <Button
              variant="text"
              startIcon={<CommonFilesIcon />}
              onClick={() => navigate('/documents')}
              data-testid="common-documents-btn"
              sx={{
                fontWeight: 600,
                textTransform: 'none',
                color: tokens.colors.text,
                '&:hover': { color: tokens.colors.accent },
              }}
            >
              공통 파일 보관함
            </Button>
            {/* QA 리스트 — 전 현장 공통 QA 항목. 조회는 누구나, 수정은 admin(페이지 안에서 가림).
                현장은 현장 페이지의 QA 메뉴에서 이 목록을 가져와 진행한다. */}
            <Button
              variant="text"
              startIcon={<QaListIcon />}
              onClick={() => navigate('/qa-template')}
              data-testid="qa-list-btn"
              sx={{
                fontWeight: 600,
                textTransform: 'none',
                color: tokens.colors.text,
                '&:hover': { color: tokens.colors.accent },
              }}
            >
              QA 리스트
            </Button>
            {/* 운영 템플릿 + 신규 현장 — 모두 admin 전용.
                backend RequireRole + frontend route guard 와 sync. */}
            {canAdmin && (
              <Button
                variant="outlined"
                startIcon={<ChecklistRtlIcon />}
                onClick={() => navigate('/operation-template')}
                disableElevation
                data-testid="operation-template-btn"
                sx={{
                  fontWeight: 600,
                  textTransform: 'none',
                  color: tokens.colors.text,
                  borderColor: tokens.colors.border,
                  '&:hover': { borderColor: tokens.colors.accent, color: tokens.colors.accent },
                }}
              >
                운영 템플릿
              </Button>
            )}
            {canAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreateClick}
                disableElevation
                data-testid="create-site-btn"
                sx={{
                  bgcolor: tokens.colors.accent,
                  color: '#fff',
                  fontWeight: 600,
                  letterSpacing: 0.2,
                  textTransform: 'none',
                  '&:hover': { bgcolor: tokens.colors.accent, filter: 'brightness(0.95)' },
                }}
              >
                신규 현장
              </Button>
            )}
            <IconButton
              onClick={toggleTheme}
              size="small"
              aria-label={mode === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
              sx={{ color: tokens.colors.text }}
            >
              {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
            <UserProfileMenu />
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default DashboardHeader;
